import { yupResolver } from '@hookform/resolvers/yup';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { LoadingButton } from '@mui/lab';
import {
  Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Menu, MenuItem, Select, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { AxiosError } from 'axios';
import { addWeeks } from 'date-fns';
import { format } from 'date-fns-tz';
import { forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import LicenseContext from 'src/contexts/LicenseContext';
import { DesignatedTruckFeature } from 'src/entities/orderEntity';
import { OtherLocation, TemporaryTruckEntity, TemporaryTruck, CustomTruckType } from 'src/entities/TemporaryTruckEntity';
import { TruckType } from 'src/entities/truckEntity';
import { useGaragesQuery } from 'src/hooks/useGarages.query';
import { useQueryCompany } from 'src/hooks/useQueryCompany';
import { useTemporaryTruckMutation } from 'src/hooks/useTemporaryTruck.mutation';
import { useVehicleAttributeValuesRequest } from 'src/hooks/useVehicleAttributeValues.request';
import Details from 'src/pages/V2TrucksPresenter/Details';
import * as yup from 'yup';

const truckSchema = yup.object({
  type: yup.string(),
  count: yup.number().required('必須です。').positive('1以上を設定してください。'),
  customType: yup.object().shape({
    weightKg: yup.number().positive(),
    volumeM3: yup.number().transform((value: number, originalValue) => (originalValue === 0 ? null : value)).positive().nullable(),
  }),
});

const schema = yup.object().shape({
  name: yup.string().nullable(),
  startAt: yup.string().required('必須項目です。'),
  endAt: yup.string().required('必須項目です。')
    .test('greater-than-start', '終了時間は開始時間以降を設定してください。', function (value) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { startAt } = this.parent;
      if (!value || !startAt) return true;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return new Date(value) >= new Date(startAt);
    }),
  trucks: yup.array().of(truckSchema).min(1, 'トラックの種類は1つ以上選択してください。'),
  nullBase: yup.bool(),
  garageId: yup.number(),
  otherLocation: yup.object().shape({
    name: yup.string(),
    address: yup.string(),
  }).test('at-leaset-one-base', '出発地を選択してください。', function (value) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { nullBase, garageId, otherLocation } = this.parent;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return nullBase === true || garageId !== undefined || Object.keys(otherLocation).length > 1;
  }),
  maximum_loading_capacity_kg: yup.number().required('必須です').transform((value: number) => (Number.isNaN(value) ? null : value)),
  /*
   * Why not integer: 体積は API が mm3 で持つので小数の m3 を表現できる（実車両の入力欄も通る）。
   * 整数に縛ると 2.2 m3 のような現実的な値が打てない
   */
  loading_platform_volume_m3: yup
    .number()
    .typeError('数値を入力してください。')
    .nullable()
    .transform((value: number) => (Number.isNaN(value) ? null : value)),
  // #329 P7 で全タイプから入力できるようにしたので、実車両マスタ（V2TrucksPresenter/schema.ts）と
  // 同じ規則で数値化する。従来は setValue でしか入らなかったため schema に無かった
  maximum_loading_capacity_quantity: yup
    .number()
    .integer('整数で入力してください')
    .positive('整数で入力してください')
    .typeError('整数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  maximum_loading_rate: yup.number()
    .nullable()
    .transform((value: number) => (Number.isNaN(value) ? null : value))
    .min(0.01, '上限積載率は0.01以上の値にしてください。')
    .max(1.0, '上限積載率は1.0以下の値にしてください。'),
  minimum_loading_rate: yup.number()
    .nullable()
    .transform((value: number) => (Number.isNaN(value) ? null : value))
    .min(0.01, '上限積載率は0.01以上の値にしてください。')
    .max(1.0, '下限積載率は1.0以下の値にしてください。')
    .test('less-than-max', '下限積載率は上限積載率以下にしてください。', function (value) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const maxRate = this.parent.maximum_loading_rate;
      if (!value || !maxRate) return true;
      return value <= maxRate;
    }),
    truck_fuel_cost_yen_per_km: yup
      .number()
      .integer('整数で入力してください')
      .positive('整数で入力してください')
      .typeError('整数で入力してください')
      .nullable()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
    truck_expressway_fee_yen_per_shift: yup
      .number()
      .integer('整数で入力してください')
      .positive('整数で入力してください')
      .typeError('整数で入力してください')
      .nullable()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
    truck_insurance_fee_yen_per_day: yup
      .number()
      .integer('整数で入力してください')
      .positive('整数で入力してください')
      .typeError('整数で入力してください')
      .nullable()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
    truck_repair_cost_yen_per_day: yup
      .number()
      .integer('整数で入力してください')
      .positive('整数で入力してください')
      .typeError('整数で入力してください')
      .nullable()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
    max_num_pistons: yup
      .number()
      .integer('整数で入力してください')
      .positive('整数で入力してください')
      .typeError('整数で入力してください')
      .nullable()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
    max_num_locations_per_pistons: yup
      .number()
      .integer('整数で入力してください')
      .positive('整数で入力してください')
      .typeError('整数で入力してください')
      .nullable()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
    sort_order: yup
      .number()
      .integer('整数で入力してください')
      .positive('整数で入力してください')
      .typeError('整数で入力してください')
      .nullable()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
});

const otherLocationSchema = yup.object({
  name: yup.string().required('必須項目です。'),
  address: yup.string().required('必須項目です。'),
});

type Props = {
  startOn: string;
  endOn: string;
  onSaved: () => void;
  onCancel: () => void;
  hideActions?: boolean;
};

export type PlanningTemporaryTruckInlineFormHandle = {
  submit: () => Promise<void>;
};

const PlanningTemporaryTruckInlineForm = memo(forwardRef<PlanningTemporaryTruckInlineFormHandle, Props>(({ startOn, endOn, onSaved, onCancel, hideActions }, ref) => {
  const { data: garages } = useGaragesQuery();
  const { create } = useTemporaryTruckMutation();
  const { data: vehicleAttributeValues } = useVehicleAttributeValuesRequest();

  const licenseContext = useContext(LicenseContext);
  /**
   * 「その他の車両」の既定値は車両タイプに紐づかないので会社に 1 組だけ持つ（#329 P7）。
   * 選択肢一覧には同梱されず、会社設定（GET /api/v2/company）に載る。
   * 一度も入力していない会社でもキーは来て値が null
   */
  const { data: companyData } = useQueryCompany(licenseContext.config?.selected_company_id);

  const [features, setFeatures] = useState<DesignatedTruckFeature[]>([]);
  const [selectedTruckType, setSelectedTruckType] = useState<TruckType>(null);
  const [truckCounts, setTruckCounts] = useState<Record<string, number>>({});
  const [customTruckType, setCustomTruckType] = useState<CustomTruckType>();
  const [customTruckWeight, setCustomTruckWegiht] = useState<number>();
  const [customTruckVolume, setCustomTruckVolume] = useState<number>();
  const [customTruckQuantity, setCustomTruckQuantity] = useState<number>();
  const [customTruckCount, setCustomTruckCount] = useState<number>();
  const [openCustomTruckType, setOpenCustomTruckType] = useState(false);
  const [garageMenuAnchorEl, setGarageMenuAnchorEl] = useState<HTMLElement>(null);
  const [nullBase, setNullBase] = useState(false);
  const [garageId, setGarageId] = useState<number>();
  const [otherLocation, setOtherLocation] = useState<OtherLocation>();
  const [otherLocationDialogOpen, setOtherLocationDialogOpen] = useState(false);
  const [truckCountErrors, setTruckCountErrors] = useState<Record<string, string>>({});
  const [customTruckCountError, setCustomTruckCountError] = useState('');
  const [minStartAt, setMinStartAt] = useState('');
  const [maxStartAt, setMaxStartAt] = useState('');
  const [minEndAt, setMinEndAt] = useState('');
  const [maxEndAt, setMaxEndAt] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectableWorkingHours, setSelectableWorkingHours] = useState<number[]>([]);
  const [showDetails, setShowDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * 車両タイプの選択肢。#329 P7 でハードコード（旧 TRUCK_KLASS_TABLE / TruckTypes）から
   * システム設定「車両表示設定」のマスタ由来に変えた。無効にした値は新規の選択肢から外す。
   *
   * Why not useMemo を外す: 下の effect の依存に入るので、毎レンダー新しい配列になると
   * 既定値の setValue が毎レンダー走り、利用者が詳細設定で直した値を上書きしてしまう。
   */
  const klassOptions = useMemo(
    () => [...(vehicleAttributeValues?.klass ?? [])]
      .filter((it) => it.active)
      .sort((a, b) => a.position - b.position),
    [vehicleAttributeValues],
  );
  const klassOptionsLoading = !vehicleAttributeValues;

  /** 直近で既定の積載値を入れた対象。同じ対象のまま選択肢一覧だけ入れ替わっても入れ直さない */
  const appliedDefaultsForRef = useRef<string | null>(null);

  /**
   * 選択中のタイプに既定の積載値が無いか（＝初回入力が必要か）。
   * 「その他の車両」は専用ダイアログで必ず重量を入れるので対象外。
   */
  const selectedTruckTypeNeedsCapacity = useMemo(() => {
    if (customTruckType || selectedTruckType === null) return false;

    const selected = klassOptions.find((it) => it.name === selectedTruckType);
    return selected != null
      && selected.default_weight_kg == null
      && selected.default_volume_m3 == null;
  }, [customTruckType, klassOptions, selectedTruckType]);

  const defaultStartAt = useMemo(() => {
    const ret = new Date(startOn);
    ret.setHours(0, 0, 0, 0);
    return format(ret, 'yyyy-MM-dd HH:mm', { timeZone: 'Asia/Tokyo' });
  }, [startOn]);

  const defaultEndAt = useMemo(() => {
    const ret = new Date(startOn);
    ret.setHours(23, 59, 59, 999);
    return format(ret, 'yyyy-MM-dd HH:mm', { timeZone: 'Asia/Tokyo' });
  }, [startOn]);

  const form = useForm<TemporaryTruckEntity>({
    resolver: yupResolver(schema),
    defaultValues: {
      startAt: defaultStartAt,
      endAt: defaultEndAt,
      nullBase: false,
      garageId: undefined,
      otherLocation: undefined,
    }
  });

  const { control, register, getValues, setValue, handleSubmit, formState: { errors }, reset } = form;
  const otherLocationForm = useForm<OtherLocation>({
    resolver: yupResolver(otherLocationSchema),
    reValidateMode: 'onBlur',
  });

  const {
    handleSubmit: handleSubmitOtherLocation,
    register: registerOtherLocation,
    formState: { errors: otherLocationErrors },
    reset: resetOtherLocation,
  } = otherLocationForm;

  const resetBase = useCallback(() => {
    setNullBase(false);
    setGarageId(undefined);
    setOtherLocation(undefined);

    setValue('nullBase', false);
    setValue('garageId', undefined);
    setValue('otherLocation', undefined);
  }, [setValue]);

  useEffect(() => {
    const startAt = new Date(startOn);
    startAt.setHours(0, 0, 0, 0);
    setMinStartAt(format(startAt, 'yyyy-MM-dd HH:mm', { timeZone: 'Asia/Tokyo' }));
    const endAt = new Date(endOn);
    endAt.setHours(23, 59, 59, 999);
    setMaxStartAt(format(endAt, 'yyyy-MM-dd HH:mm', { timeZone: 'Asia/Tokyo' }));

    reset();
    setValue('trucks', []);
    resetOtherLocation();
    setSelectedTruckType(null);
    setTruckCounts({});
    setFeatures([]);
    setCustomTruckType(null);
    resetBase();
  }, [startOn, endOn, reset, resetOtherLocation, resetBase, setValue]);

  // 台数の入力ごとに走る。trucks の同期だけを担う
  useEffect(() => {
    let truck: TemporaryTruck = null;
    if (customTruckType) {
      truck = {
        customType: customTruckType,
        count: customTruckCount,
      };
    } else if (selectedTruckType !== null) {
      truck = {
        type: selectedTruckType,
        count: truckCounts[selectedTruckType],
      };
    }
    setValue('trucks', truck ? [truck] : []);
  }, [customTruckCount, customTruckType, selectedTruckType, setValue, truckCounts]);

  /**
   * 選択が変わったときだけ既定の積載値を入れる。
   *
   * Why not trucks の同期と同じ effect にする: 依存に truckCounts が入るので、
   * 台数を打つたびに既定値が再適用され、利用者が直した積載値を消してしまう
   * （旧 TRUCK_KLASS_TABLE 時代からの既存バグ）。PM 回答の
   * 「値は仮車両作成時に編集可能とする」を満たすために分けている。
   *
   * Why not 依存の klassOptions を外す: 登録に成功すると useTemporaryTruck.mutation が
   * 選択肢一覧を invalidate するので（#329 P7・学習 B）、再取得で klassOptions の参照が変わり
   * 同じ選択のまま既定値が入り直す。依存から外すと lint を黙らせるだけで、後から依存が
   * 増えたときに同じ事故へ戻る。「何に対して既定値を入れたか」で判定する。
   * 1 件でも失敗するとダイアログは開いたまま残る（ConvenienceDispatchAddVehicleDialog.tsx:666）ので、
   * 「成功したら閉じるから踏まない」では済まない。
   */
  useEffect(() => {
    const appliedFor = customTruckType
      ? `custom:${customTruckType.weightKG}:${customTruckType.volumeM3}:${customTruckType.quantity ?? ''}`
      : selectedTruckType;

    if (appliedFor === null) {
      // 選択を外したら、選び直したときにまた既定値から始められるようにする
      appliedDefaultsForRef.current = null;
      return;
    }
    if (appliedDefaultsForRef.current === appliedFor) return;
    appliedDefaultsForRef.current = appliedFor;

    if (customTruckType) {
      setValue('maximum_loading_capacity_kg', customTruckType.weightKG);
      setValue('loading_platform_volume_m3', customTruckType.volumeM3);
      setValue('maximum_loading_capacity_quantity', customTruckType.quantity ?? null);
      setValue('klass', null);
      return;
    }

    // 既定値はマスタ（vehicle_attribute_values）から引く。会社が足したタイプには
    // 既定値が無いので null を入れ、利用者に初回入力を促す（#329 P7）
    const selected = klassOptions.find((it) => it.name === selectedTruckType);
    setValue('maximum_loading_capacity_kg', selected?.default_weight_kg ?? null);
    setValue('loading_platform_volume_m3', selected?.default_volume_m3 ?? null);
    setValue('maximum_loading_capacity_quantity', null);
    // API の klass は「名前」で検証されるので、id ではなく表示名を送る
    setValue('klass', selectedTruckType);
  }, [customTruckType, klassOptions, selectedTruckType, setValue]);

  const handleCountChange = (truckType: string, value: string) => {
    setTruckCounts((prev) => ({
      ...prev,
      [truckType]: Number(value),
    }));
  };

  /**
   * 「その他の車両」のダイアログを開く。会社の既定値を初期表示にする。
   *
   * Why not effect で会社設定の到着時に入れる: 利用者が打った値を後から上書きしかねない。
   * 開くときに「まだ何も入っていないときだけ」入れる形にしてある
   * （クリアすると未入力へ戻るので、開き直すとまた会社の既定値から始まる）
   */
  const handleOpenCustomTruckType = () => {
    setCustomTruckWegiht((prev) => prev ?? companyData?.other_vehicle_default_weight_kg ?? undefined);
    setCustomTruckVolume((prev) => prev ?? companyData?.other_vehicle_default_volume_m3 ?? undefined);
    setOpenCustomTruckType(true);
  };

  const handleAddCustomTruckType = () => {
    setCustomTruckType({
      weightKG: customTruckWeight,
      volumeM3: customTruckVolume,
      quantity: customTruckQuantity ?? null,
    });
    setOpenCustomTruckType(false);
  };

  const handleCloseCustomTruckType = () => {
    setOpenCustomTruckType(false);
  };

  const handleCloseGarageMenu = () => {
    setGarageMenuAnchorEl(null);
  };

  const handleClearCustomTruckType = () => {
    setCustomTruckType(undefined);
    setCustomTruckWegiht(undefined);
    setCustomTruckVolume(undefined);
    setCustomTruckQuantity(undefined);
    setOpenCustomTruckType(false);
  };

  const handleSelectGarage = (id: number) => {
    resetBase();
    setGarageId(id);
    setValue('garageId', id);
    setGarageMenuAnchorEl(null);
  };

  const handleSelectNullBase = () => {
    resetBase();
    setNullBase(true);
    setValue('nullBase', true);
  };

  const handleAddOtherLocation = () => {
    resetBase();
    setOtherLocation(otherLocationForm.getValues());
    setValue('otherLocation', otherLocationForm.getValues());
    setOtherLocationDialogOpen(false);
  };

  useEffect(() => {
    setTruckCountErrors({});
    setCustomTruckCountError('');
    if (!(errors && errors.trucks)) return;

    const obj: Record<string, string> = {};
    if (errors.trucks.length > 0) {
      obj[selectedTruckType] = errors.trucks[0].count?.message;
    }
    setTruckCountErrors(obj);
    if (customTruckType) {
      setCustomTruckCountError(errors.trucks[0].count?.message);
    }
  }, [customTruckType, errors, selectedTruckType]);

  useEffect(() => {
    const startAtValue = getValues('startAt');
    if (startAtValue) {
      setMinEndAt(startAtValue);
      setMaxEndAt(format(addWeeks(new Date(startAtValue), 2), 'yyyy-MM-dd HH:mm'));
    }
  }, [getValues]);

  // 二重 POST 防止用ガード (state は閉包が古いので ref で同期チェック)
  const submittingRef = useRef(false);
  // 1 度成功したらこのフォームインスタンスでは以降 submit を一切受けつけない。
  // 親 dialog の ref Map が delete + 再 set で iterator から再訪問されたとしても
  // 同じ完成済みフォームに対する 2 回目の POST は完全に塞ぐ。
  const completedRef = useRef(false);

  const onSubmitSave: SubmitHandler<TemporaryTruckEntity> = useCallback(async (data: TemporaryTruckEntity): Promise<void> => {
    if (submittingRef.current || completedRef.current) return;
    submittingRef.current = true;
    setErrorMessage('');
    data.workingAvailableDurationSeconds = data.workingAvailableDurationHours * 60 * 60;
    setIsLoading(true);
    try {
      await create.mutateAsync(data);
      completedRef.current = true;
      onSaved();
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      setErrorMessage(axiosError.response.data.message);
      throw error;
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  }, [create, onSaved]);

  useImperativeHandle(ref, () => ({
    submit: () => new Promise<void>((resolve, reject) => {
      // 多重 invoke / 完了後の再 invoke いずれも POST しない
      if (submittingRef.current || completedRef.current) {
        resolve();
        return;
      }
      void handleSubmit(
        async (data) => { try { await onSubmitSave(data); resolve(); } catch (e) { reject(e as Error); } },
        () => reject(new Error('バリデーションエラー'))
      )();
    }),
  }), [handleSubmit, onSubmitSave]);

  const resetSelectableWorkingHours = useCallback(() => {
    const startAt = getValues('startAt');
    const endAt = getValues('endAt');

    const diffMicroSeconds = new Date(endAt).getTime() - new Date(startAt).getTime();
    if (Number.isNaN(diffMicroSeconds)) {
      setSelectableWorkingHours([]);
      return;
    }

    const diffHours = Math.min(Math.round(Math.abs(diffMicroSeconds) / (60 * 60 * 1000)), 23);
    setSelectableWorkingHours(Array.from({ length: diffHours }, (_, i) => i + 1));
  }, [getValues]);

  return (
    <Box>
      {errorMessage && (
        <Typography variant="body1" color="red">{errorMessage}</Typography>
      )}
      <Stack component="form">
        <Stack direction="row" spacing={2} sx={{ pt: 1, mb: 1 }}>
          <Typography variant="h6">
            トラック種類・台数
          </Typography>
          {errors?.trucks?.message && (
            <Typography variant="body1" color="red">
              {errors?.trucks?.message}
            </Typography>
          )}
        </Stack>
        <Stack spacing={2} direction="row" sx={{ ml: 2, }} data-testid="temporary-truck-types">
          {/*
            読み込み中は選択肢を出さない。
            Why not その他の車両だけ先に出す: 読み込みが終わるまで並びが確定しないので、
            ボタンがあとから割り込んで押し間違いを誘発する
          */}
          {klassOptionsLoading && (
            <Typography variant="body2" color="text.secondary">読み込み中...</Typography>
          )}
          {!klassOptionsLoading && klassOptions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              車両タイプが登録されていません。システム設定の「車両表示設定」で追加してください。
            </Typography>
          )}
          {!klassOptionsLoading && klassOptions.map(({ id, name: truckType }) => (
            <Stack key={id}>
              <ToggleButtonGroup
                value={selectedTruckType}
                onChange={() => {
                  setSelectedTruckType(truckType);
                }}
                sx={{ flexWrap: 'wrap', }}
              >
                <ToggleButton
                  sx={{ width: '80px', m: 0, textTransform: 'none', }}
                  value={truckType}
                >
                  {truckType}
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          ))}
          {!klassOptionsLoading && (
          <Stack key="customTruckInputs">
            <Button
              sx={{ p: 1.1, width: '150px' }}
              variant={customTruckType ? 'contained' : 'outlined'}
              onClick={handleOpenCustomTruckType}
            >
              {customTruckType ? `${customTruckType.weightKG}kg` : 'その他の車両'}
            </Button>
            <Dialog open={openCustomTruckType}>
              <DialogTitle>その他の車両</DialogTitle>
              <DialogContent>
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography>重量(kg)</Typography>
                    <TextField
                      value={customTruckWeight || ''}
                      onChange={(e) => setCustomTruckWegiht(Number(e.target.value))}
                      placeholder="重量を入力"
                      variant="standard"
                      type="number"
                    />
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography>体積(m3)</Typography>
                    <TextField
                      value={customTruckVolume || ''}
                      onChange={(e) => setCustomTruckVolume(Number(e.target.value))}
                      placeholder="体積を入力"
                      variant="standard"
                      type="number"
                    />
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography>数量</Typography>
                    <TextField
                      value={customTruckQuantity ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') {
                          setCustomTruckQuantity(undefined);
                          return;
                        }
                        const parsed = parseInt(v, 10);
                        setCustomTruckQuantity(Number.isFinite(parsed) ? parsed : undefined);
                      }}
                      placeholder="数量を入力 (任意)"
                      variant="standard"
                      type="number"
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Stack>
                </Stack>
              </DialogContent>
              <DialogActions>
                {customTruckType && (
                  <Button onClick={handleClearCustomTruckType}>クリア</Button>
                )}
                <Button onClick={handleCloseCustomTruckType}>キャンセル</Button>
                <Button
                  onClick={handleAddCustomTruckType}
                  disabled={!customTruckWeight}
                  variant="contained"
                >
                  追加
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
          )}
        </Stack>
        {/*
          PM 回答「車両タイプ選択時に台数・数量・最大積載重量・最大積載体積を設定可能とする」。
          台数はタイプごとのボタン直下に残し（エラーもそこに出る既存挙動）、
          残り 3 つをここに出す。ラベルと体積欄の出し分けは既存の Details に合わせる
        */}
        {(selectedTruckType !== null || customTruckType) && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ ml: 2, mt: 2 }}
            data-testid="temporary-truck-capacity"
          >
            {/*
              Why not id を固定する: /convenience-dispatches と /map はこのフォームを
              1 ダイアログに複数並べるので、固定すると id が衝突して
              2 台目のラベルを押しても 1 台目にフォーカスが行く。MUI の自動生成に任せる
            */}
            <TextField
              size="small"
              label="台数"
              type="number"
              value={customTruckType ? (customTruckCount || '') : (truckCounts[selectedTruckType] || '')}
              onChange={(e) => (customTruckType
                ? setCustomTruckCount(Number(e.target.value))
                : handleCountChange(selectedTruckType, e.target.value))}
              error={customTruckType ? !!customTruckCountError : !!truckCountErrors[selectedTruckType]}
              helperText={customTruckType ? customTruckCountError : truckCountErrors[selectedTruckType]}
              FormHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="数量"
              type="text"
              {...register('maximum_loading_capacity_quantity')}
              error={'maximum_loading_capacity_quantity' in errors}
              helperText={errors.maximum_loading_capacity_quantity?.message}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label={`最大積載重量(${licenseContext.config?.unit})`}
              type="text"
              required
              {...register('maximum_loading_capacity_kg')}
              error={'maximum_loading_capacity_kg' in errors}
              helperText={errors.maximum_loading_capacity_kg?.message}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            {licenseContext.config?.unit_used_for_calculation === '重量と体積' && (
              <TextField
                size="small"
                label="最大積載体積(m³)"
                type="text"
                {...register('loading_platform_volume_m3')}
                error={'loading_platform_volume_m3' in errors}
                helperText={errors.loading_platform_volume_m3?.message}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            )}
          </Stack>
        )}
        {selectedTruckTypeNeedsCapacity && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mt: 1 }}>
            この車両タイプには初期値がありません。最大積載量を入力してください。
          </Typography>
        )}
        {/*
          入力値は次回以降の既定値として上書き保存される（#329 P7・学習 B）。
          会社全体に効くことが画面から分からないので添える
        */}
        {(selectedTruckType !== null || customTruckType) && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mt: 0.5 }}>
            初期値は車両タイプごとに保持されます。ここで変更した値がこの仮車両に適用されます。
          </Typography>
        )}
        <Stack sx={{ mt: 1 }}>
          <Typography variant="h6">稼働時間</Typography>
          <Stack sx={{ pb: 2, ml: 2, mt: 1, }} direction="row" spacing={2} alignItems="flex-start">
            <TextField
              required
              size="small"
              id="startAt"
              label="開始時間"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              {...register('startAt')}
              defaultValue={getValues('startAt')}
              error={'startAt' in errors}
              helperText={errors.startAt?.message}
              inputProps={{
                min: minStartAt,
                max: maxStartAt,
              }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              required
              size="small"
              id="endAt"
              label="終了時間"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              {...register('endAt')}
              defaultValue={getValues('endAt')}
              error={'endAt' in errors}
              helperText={errors.endAt?.message}
              inputProps={{
                min: minEndAt,
                max: maxEndAt,
              }}
              sx={{ minWidth: 150 }}
            />
            <Controller
              name="workingAvailableDurationHours"
              control={control}
              render={(({ field }) => (
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel shrink>労働時間</InputLabel>
                  <Select
                    {...field}
                    {...register('workingAvailableDurationHours')}
                    value={getValues('workingAvailableDurationHours') ?? ''}
                    label="労働時間"
                    notched
                    size="small"
                    onOpen={resetSelectableWorkingHours}
                  >
                    <MenuItem value="">
                      未指定
                    </MenuItem>
                    {selectableWorkingHours.map((hour) => (
                      <MenuItem key={['workingAvailableDurationHours', hour].join('-')} value={hour}>
                        {hour}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            />
          </Stack>
        </Stack>
        <Stack>
          <Stack direction="row" spacing={2}>
            <Typography variant="h6">
              出発地を選択
            </Typography>
            {errors?.otherLocation?.message && (
              <Typography variant="body1" color="red">
                {errors?.otherLocation?.message}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 1, ml: 2, }}>
            <Button
              key="nullBaseButton"
              variant={nullBase ? 'contained' : 'outlined'}
              onClick={handleSelectNullBase}
            >
              指定なし
            </Button>
            <Button
              key="selectGarageButton"
              variant={garageId ? 'contained' : 'outlined'}
              onClick={(e) => setGarageMenuAnchorEl(e.currentTarget)}
            >
              {garageId ? garages.find((it) => it.id === garageId).name : '車庫を選択'}
            </Button>
            <Menu
              open={Boolean(garageMenuAnchorEl)}
              anchorEl={garageMenuAnchorEl}
              onClose={handleCloseGarageMenu}
              key="selectGarageMenu"
              PaperProps={{
                style: { padding: 0, margin: 0, }
              }}
            >
              <List sx={{ p: 0, m: 0 }}>
                {garages?.map((garage) => (
                  <ListItemButton
                    key={['selectGarage', 'menuItem', garage.id].join('-')}
                    selected={garageId === garage.id}
                    onClick={() => handleSelectGarage(garage.id)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <ListItemText
                      primary={garage.name}
                      secondary={garage.address}
                      primaryTypographyProps={{ fontSize: 14, color: 'black', }}
                      secondaryTypographyProps={{ fontSize: 12, }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Menu>
            <Button
              key="otherLocationButton"
              variant={otherLocation ? 'contained' : 'outlined'}
              onClick={() => setOtherLocationDialogOpen(true)}
            >
              {otherLocation ? otherLocation.name : 'その他の場所'}
            </Button>
            <Dialog
              open={otherLocationDialogOpen}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>その他の場所</DialogTitle>
              <DialogContent>
                <Stack component="form">
                  <FormControl>
                    <TextField
                      required
                      margin="dense"
                      id="otherLocationName"
                      label="名称"
                      type="text"
                      variant="standard"
                      {...registerOtherLocation('name')}
                      error={'name' in otherLocationErrors}
                      helperText={otherLocationErrors?.name?.message}
                    />
                    <TextField
                      required
                      margin="dense"
                      id="otherLocationAddress"
                      label="アドレス"
                      type="text"
                      variant="standard"
                      {...registerOtherLocation('address')}
                      error={'address' in otherLocationErrors}
                      helperText={otherLocationErrors?.address?.message}
                    />
                  </FormControl>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOtherLocationDialogOpen(false)}>キャンセル</Button>
                <Button
                  type="submit"
                  variant="contained"
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={handleSubmitOtherLocation(handleAddOtherLocation)}
                >
                  追加
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="row" sx={{ alignItems: 'center', mt: 2, }}>
        <IconButton
          aria-label="expand row"
          onClick={() => setShowDetails((prev) => !prev)}
        >
          {showDetails ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
        <Typography onClick={() => setShowDetails((prev) => !prev)}>詳細設定</Typography>
      </Stack>
      <Collapse in={showDetails} timeout="auto" unmountOnExit>
        <Details<TemporaryTruckEntity>
          form={form}
          control={control}
          register={register}
          errors={errors}
          unit={licenseContext.config?.unit}
          unitUsedForCalculation={licenseContext.config?.unit_used_for_calculation}
          features={features}
          setFeatures={setFeatures}
          // 車両タイプと積載量は上の「トラック種類・台数」で設定するので詳細設定には出さない
          truckTypeAndCapacitySetOutside
        />
      </Collapse>
      {!hideActions && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
          <LoadingButton onClick={onCancel} loading={isLoading}>キャンセル</LoadingButton>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <LoadingButton type="submit" variant="contained" onClick={handleSubmit(onSubmitSave)} loading={isLoading}>保存</LoadingButton>
        </Stack>
      )}
    </Box>
  );
}));

PlanningTemporaryTruckInlineForm.displayName = 'PlanningTemporaryTruckInlineForm';

export default PlanningTemporaryTruckInlineForm;
